import moment from 'moment';
import toastr from 'toastr';
import { resendDelivery } from '../../../../services/clinicPortalServices/orderSearchService';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { Modal, Button } from 'react-bootstrap';
import { getOrderDataById } from '../../../../services/clinicPortalServices/orderEditService';
import {
    getFullAddress,
    getUserAuthToken,
    getRowColumnIndexForQuantPlate,
    dateComparator,
    isUsingInternalSample
} from '../../../../services/common/util';
import { serviceConstants } from '../../../../services/common/constants';
import { quantPlateRows, quantPlateColumns } from '../../../../services/common/optionsData';
import { AgGridReact } from 'ag-grid-react';
import EditBtnCellRenderer from '../../requisitions/clinicRequisitionGrid/editBtnCellRenderer';
import { ModalStyled } from '../../../../theme/customizedStyleComponents';
import { ThemeContext } from '../../../../theme/ThemeProvider';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import { generateAiNarrative, getAiNarrative } from '../../../../services/clinicPortalServices/orderSearchService';
import PatientViewPopup from '../../patients/clinicPatientGrid/patientViewPopup';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

/* ── Full Information side-panel drawer ── */
export const SubOrderDrawer = ({ orderDetails, onClose }) => {
    const [details, setDetails] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const id = orderDetails && (orderDetails._id || orderDetails.order_id);
        if (!id) { setLoading(false); return; }
        setLoading(true);
        getOrderDataById(id).then((res) => {
            if (res && res.data) setDetails(res.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [orderDetails && (orderDetails._id || orderDetails.order_id)]);

    const d = details || {};
    const patient = d.patient_id || {};
    const results = d.results || {};
    const testInfo = d.test_info || {};
    const provider = d.provider || {};
    const ins = d.patient_insurance_id || {};

    const fmt = (v) => v ? moment(v, 'YYYYMMDDHHmmss').format('MM/DD/YYYY hh:mm A') : '—';
    const token = getUserAuthToken();
    const ts = new Date().getTime();

    const sampleTrackingStrings = (d.sample_tracking || []).map(item =>
        moment(item.time, 'YYYYMMDDHHmmss').format('MM/DD/YYYY hh:mm A') + ' — ' + item.eventName
    );
    const notificationStrings = (d.notification_log || []).map(item =>
        moment(item.time, 'YYYYMMDDHHmmss').format('MM/DD/YYYY hh:mm A') + ' — ' + item.notificationType + ' sent to ' + item.email
    );

    return ReactDOM.createPortal(
        <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0,
            width: '780px', maxWidth: '95vw',
            background: '#fff', boxShadow: '-4px 0 32px rgba(0,0,0,0.18)',
            zIndex: 2000, display: 'flex', flexDirection: 'column',
            animation: 'slideInRight 0.22s ease'
        }}>
            <style>{`@keyframes slideInRight{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>

            {/* Header */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px', borderBottom: '1px solid #dee2e6',
                background: '#f8f9fa', flexShrink: 0
            }}>
                <div>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Full Information</div>
                    {d.lab_order_id && <div style={{ fontSize: '0.82rem', color: '#6c757d' }}>{d.lab_order_id}</div>}
                </div>
                <button onClick={onClose} className="btn btn-sm btn-outline-secondary"
                    style={{ fontSize: '1.2rem', lineHeight: 1, padding: '3px 10px' }} title="Close panel">×</button>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '18px 22px', fontSize: '0.9rem' }}>
                {loading && <div className="text-center py-4"><span className="spinner-border text-primary" /></div>}
                {!loading && d && patient.first_name && (
                    <>
                        {/* Status row */}
                        <div className="mb-3">
                            <b>Status:</b> Order - {d.status}
                            {d.order_type === 'panel' ? ' (Panel)' : d.order_type === 'sub_panel' ? ' (Part of Panel)' : ''}
                        </div>

                        {/* Two-column header info */}
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <div><b>Requisition Number:</b> <span style={{ color: '#0d6efd' }}>{d.lab_order_id}</span></div>
                                <div><b>Sample ID:</b> <span style={{ color: '#0d6efd' }}>{testInfo.sample}</span></div>
                                {testInfo.received && <div><b>Received Date:</b> {fmt(testInfo.received)}</div>}
                                {results.released && <div><b>Released Date:</b> {fmt(results.released)}</div>}
                                {results.pdf_path && (
                                    <div className="mt-1">
                                        <b>Result: </b>
                                        <a href={serviceConstants.API_HOST_NAME + results.pdf_path + '?' + ts + '&token=' + token}
                                            target="_blank" rel="noreferrer" style={{ textDecoration: 'underline' }}>
                                            View PDF
                                        </a>
                                    </div>
                                )}
                                {d.is_cancel && d.cancel_reason && <div><b>Cancelled Reason:</b> {d.cancel_reason}</div>}
                            </div>
                            <div className="col-md-6">
                                <div><b>Patient:</b> <span style={{ color: '#0d6efd' }}>{patient.first_name} {patient.last_name}</span>
                                    {patient.date_of_birth ? ' (' + moment(patient.date_of_birth, 'YYYY-MM-DD').format('MM/DD/YYYY') + ')' : ''}</div>
                                <div><b>Patient ID:</b> <span style={{ color: '#0d6efd' }}>{patient.mrn}</span></div>
                                <div><b>Phone/Email:</b> {patient.mobile} / {patient.email || ''}</div>
                                <div><b>Address:</b> {getFullAddress(patient.address)}</div>
                                <div><b>Driver License:</b> {patient.driver_license} <b>State:</b> {patient.driver_license_state}</div>
                            </div>
                        </div>

                        <hr />

                        {/* Order details */}
                        <h5 className="mb-2">Order Information</h5>
                        <div className="mb-1"><b>Test:</b> {d.test_id ? d.test_id.name : ''}</div>
                        <div className="mb-1"><b>Test Type:</b> {testInfo.specimenType}</div>
                        {testInfo.collected && <div className="mb-1"><b>Specimen Collected Date:</b> {fmt(testInfo.collected)}</div>}
                        <div className="mb-1"><b>Facility Source:</b> {d.facility_source}</div>
                        {provider.first_name && <div className="mb-1"><b>Physician:</b> {provider.first_name} {provider.last_name}</div>}
                        {d.diagnosis_code && d.diagnosis_code.length > 0 && (
                            <div className="mb-1"><b>Diagnosis Codes:</b> {d.diagnosis_code.join(', ')}</div>
                        )}
                        {d.symptoms && d.symptoms.length > 0 && (
                            <div className="mb-1"><b>Patient Symptoms:</b> {d.symptoms.join(', ')}</div>
                        )}

                        {/* Medications */}
                        {d.medicationIds && d.medicationIds.length > 0 && (
                            <div className="mt-2 mb-1">
                                <b>Medications:</b>
                                {d.medicationIds.map((m, i) => (
                                    <div key={i} className="ml-2">
                                        {m.description}{m.diagnosisCodes && m.diagnosisCodes.length > 0 ? ' (' + m.diagnosisCodes.join(', ') + ')' : ''}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Notification logs */}
                        <div className="mt-2"><b>Notification Logs:</b></div>
                        {notificationStrings.length > 0
                            ? notificationStrings.map((s, i) => <div key={i} className="ml-2" style={{ fontSize: '0.85rem' }}>{s}</div>)
                            : <div className="ml-2 text-muted" style={{ fontSize: '0.85rem' }}>None</div>}

                        {/* Sample tracking */}
                        <div className="mt-2"><b>Sample Tracking:</b></div>
                        {sampleTrackingStrings.length > 0
                            ? sampleTrackingStrings.map((s, i) => <div key={i} className="ml-2" style={{ fontSize: '0.85rem' }}>{s}</div>)
                            : <div className="ml-2 text-muted" style={{ fontSize: '0.85rem' }}>None</div>}

                        {/* Requisition PDF */}
                        {d.requisition_pdf_path && (
                            <div className="mt-2">
                                <b>Requisition PDF: </b>
                                <a href={serviceConstants.API_HOST_NAME + '/files/requisitionPDF/' + d.requisition_pdf_path + '?' + ts + '&token=' + token}
                                    target="_blank" rel="noreferrer" style={{ textDecoration: 'underline' }}>
                                    {d.requisition_pdf_path}
                                </a>
                            </div>
                        )}

                        {/* Result values */}
                        {results.values && results.values.length > 0 && (
                            <>
                                <hr />
                                <h5>Result Values</h5>
                                <table className="table table-sm table-bordered">
                                    <tbody>
                                        {results.values.map((v, i) => (
                                            <tr key={i}>
                                                <td style={{ fontWeight: 600, width: '55%' }}>{v.analyte || v.name || v.code}</td>
                                                <td>{v.value}{v.unit ? ' ' + v.unit : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                        {/* Payment / Insurance */}
                        {ins && ins.insurance_provider && (
                            <>
                                <hr />
                                <h5>Payment Information</h5>
                                <div className="mb-1"><b>Insurance Provider:</b> {ins.insurance_provider}</div>
                                <div className="mb-1"><b>Member ID:</b> {ins.insured_member_id}</div>
                                <div className="mb-1"><b>Group:</b> {ins.insured_group_number}</div>
                                <div className="mb-1"><b>Holder:</b> {ins.insured_first_name} {ins.insured_last_name}</div>
                            </>
                        )}
                    </>
                )}
                {!loading && (!d || !patient.first_name) && (
                    <div className="text-muted py-4 text-center">No order data available.</div>
                )}
            </div>

            {/* Footer */}
            <div style={{ borderTop: '1px solid #dee2e6', padding: '10px 20px', flexShrink: 0, background: '#f8f9fa' }}>
                <button className="btn btn-secondary btn-sm" onClick={onClose}>Close Panel</button>
            </div>
        </div>,
        document.body
    );
};

const SubOrderActionRenderer = (props) => {
    const { data, context } = props;
    return (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', paddingTop: '2px' }}>
            <button
                className="btn btn-sm btn-outline-secondary"
                title="View in place"
                onClick={() => context.componentParent.setState({ orderDetails: data, rowData: [] })}
            >
                <i className="fas fa-folder-open"></i>
            </button>
        </div>
    );
};

export default class OrderViewPopup extends Component {
    constructor(props) {
        super(props);
        this.state = {
            uploadedFiles: [],
            quickFilterText: '',
            orderDetails: {},
            orderDetailsLoading: false,
            showPatientPopup: false,
            showSubOrderDrawer: false,
            subOrderDetails: null,
            requisitionNumber: '',
            rowData: [],
            aiNarrative: '',
            aiNarrativeGeneratedAt: null,
            aiNarrativeModel: '',
            aiNarrativeLoading: false,
            aiNarrativeError: '',
            defaultColDef: {
                flex: 1,
                filter: true,
                sortable: true,
                resizable: true
            },
            columnDefs: [
                {
                    headerName: 'Action',
                    minWidth: 110,
                    cellRenderer: 'subOrderActionRenderer'
                },
                {
                    headerName: 'External Order Id',
                    minWidth: 100,
                    field: 'external_sample_id'
                },
                {
                    headerName: 'Sample',
                    minWidth: 100,
                    field: 'test_info.sample',
                    valueGetter: (params) => {
                        if (
                            isUsingInternalSample() &&
                            params.data.external_sample_id === params.data.test_info.sample
                        ) {
                            return '';
                        } else {
                            return params.data.test_info.sample;
                        }
                    }
                },
                {
                    headerName: 'Collected Date',
                    minWidth: 50,
                    valueGetter: (params) => {
                        if (params.data.test_info.collected) {
                            return moment(params.data.test_info.collected, 'YYYYMMDDHHmmss').format(
                                'MM/DD/YYYY hh:mm A'
                            );
                        }
                    },
                    dateComparator: dateComparator
                },
                {
                    headerName: 'Test Name',
                    minWidth: 100,
                    field: 'test_id.name'
                }
            ],
            paginationNumberFormatter: function (params) {
                return '[' + params.value.toLocaleString() + ']';
            },
            context: { componentParent: this }
        };
    }

    componentDidMount() {}

    componentDidUpdate(prevProps) {
        if (this.props.show && this.props.show !== prevProps.show) {
            // Reset AI narrative when a new order is opened
            this.setState({
                aiNarrative: '',
                aiNarrativeGeneratedAt: null,
                aiNarrativeModel: '',
                aiNarrativeError: ''
            });

            if (this.props.orderDetails && this.props.orderDetails._id) {
                // Try to load any previously stored narrative
                getAiNarrative(this.props.orderDetails._id).then((response) => {
                    if (response && response.body && response.body.narrative) {
                        this.setState({
                            aiNarrative: response.body.narrative,
                            aiNarrativeGeneratedAt: response.body.generatedAt,
                            aiNarrativeModel: response.body.model
                        });
                    }
                }).catch(() => {/* no narrative yet - that's fine */});

                if (this.props.type === 'universal_search') {
                    this.setState({ orderDetails: {}, orderDetailsLoading: true });
                    getOrderDataById(this.props.orderDetails._id).then((response) => {
                        if (response && response.data) {
                            let orderDetails = response.data;
                            this.setState({ orderDetails: orderDetails, orderDetailsLoading: false });
                            let requisitionNumber = '';
                            if (orderDetails.order_type === 'root') {
                                requisitionNumber = orderDetails.lab_order_id;
                            } else if (orderDetails.parent_id && orderDetails.parent_id.order_type === 'root') {
                                requisitionNumber = orderDetails.parent_id.lab_order_id;
                            } else if (
                                orderDetails.parent_id &&
                                orderDetails.parent_id.parent_id &&
                                orderDetails.parent_id.parent_id.order_type === 'root'
                            ) {
                                requisitionNumber = orderDetails.parent_id.parent_id.lab_order_id;
                            }
                            this.setState({ requisitionNumber: requisitionNumber });
                            if (orderDetails.orderIds && orderDetails.orderIds.length) {
                                orderDetails.orderIds.map((item) => {
                                    item.parent_id = orderDetails;
                                    return item;
                                });
                                this.setState({ rowData: orderDetails.orderIds });
                            }
                        }
                    }).catch(() => {
                        this.setState({ orderDetailsLoading: false });
                    });
                } else {
                    this.setState({ orderDetails: this.props.orderDetails });
                    let requisitionNumber = '';
                    if (this.props.orderDetails.order_type === 'root') {
                        requisitionNumber = this.props.orderDetails.lab_order_id;
                    } else if (
                        this.props.orderDetails.parent_id &&
                        this.props.orderDetails.parent_id.order_type === 'root'
                    ) {
                        requisitionNumber = this.props.orderDetails.parent_id.lab_order_id;
                    } else if (
                        this.props.orderDetails.parent_id &&
                        this.props.orderDetails.parent_id.parent_id &&
                        this.props.orderDetails.parent_id.parent_id.order_type === 'root'
                    ) {
                        requisitionNumber = this.props.orderDetails.parent_id.parent_id.lab_order_id;
                    }
                    this.setState({ requisitionNumber: requisitionNumber });
                    if (this.props.orderDetails.orderIds && this.props.orderDetails.orderIds.length) {
                        this.props.orderDetails.orderIds.map((item) => {
                            item.parent_id = this.props.orderDetails;
                            return item;
                        });
                        this.setState({ rowData: this.props.orderDetails.orderIds });
                    }
                }
            }
        }

        // If parent passes updated requisition PDF paths, update our state so UI refreshes
        if (
            this.props.requisitionPdfPaths &&
            prevProps.requisitionPdfPaths !== this.props.requisitionPdfPaths
        ) {
            this.setState({ uploadedFiles: this.props.requisitionPdfPaths });
        }
    }

    handleGenerateNarrative = () => {
        const orderId = this.state.orderDetails._id;
        if (!orderId) return;
        this.setState({ aiNarrativeLoading: true, aiNarrativeError: '' });
        generateAiNarrative(orderId)
            .then((response) => {
                if (response && response.body && response.body.narrative) {
                    this.setState({
                        aiNarrative: response.body.narrative,
                        aiNarrativeGeneratedAt: response.body.generatedAt,
                        aiNarrativeModel: response.body.model,
                        aiNarrativeLoading: false
                    });
                } else {
                    this.setState({
                        aiNarrativeError: (response && response.message) || 'Failed to generate narrative.',
                        aiNarrativeLoading: false
                    });
                }
            })
            .catch((err) => {
                this.setState({
                    aiNarrativeError: err.message || 'Error generating narrative.',
                    aiNarrativeLoading: false
                });
            });
    };

    render() {
        let notificatinLogStrings = [];
        let orderNoticeHistoryStrings = [];
        let sampleTrackingStrings = [];
        if (this.state.orderDetails.notification_state) {
            switch (this.state.orderDetails.notification_state) {
                case 'NO_ACTION':
                    if (
                        this.state.orderDetails.results &&
                        ['Positive SARS-CoV-2', 'SARS-CoV-2 POSITIVE'].includes(this.state.orderDetails.results.value)
                    ) {
                        notificatinLogStrings = [
                            moment(this.state.orderDetails.results.released, 'YYYYMMDDHHmmss').format(
                                'MM/DD/YYYY hh:mm A'
                            ) + ' Delivered Positive Notification to Physician'
                        ];
                    }
                    break;
                case 'SENT_TO_PATIENT':
                    notificatinLogStrings = [
                        moment(this.state.orderDetails.notification_start_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Sent to Patient - Failed'
                    ];
                    break;
                case 'DELIVERED_TO_PATIENT':
                    notificatinLogStrings = [
                        moment(this.state.orderDetails.notification_start_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Sent to Patient',
                        moment(this.state.orderDetails.notification_checked_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Delivered to Patient - Success'
                    ];
                    break;
                case 'SENT_TO_FACILITY':
                    notificatinLogStrings = [
                        moment(this.state.orderDetails.notification_start_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Sent to Facility',
                        moment(this.state.orderDetails.notification_checked_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Delivered to Facility - Success'
                    ];
                    break;
                case 'SENT_TO_PHYSICIAN':
                    notificatinLogStrings = [
                        moment(this.state.orderDetails.notification_start_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Sent to Patient',
                        moment(this.state.orderDetails.notification_checked_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Sent to Physician'
                    ];
                    break;
                case 'DELIVERED_TO_PHYSICIAN':
                    notificatinLogStrings = [
                        moment(this.state.orderDetails.notification_start_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Sent to Patient',
                        moment(this.state.orderDetails.notification_checked_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Sent to Physician',
                        moment(this.state.orderDetails.notification_checked_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Delivered to Physician'
                    ];
                    break;
                case 'SENT_TO_SUPERADMIN':
                    notificatinLogStrings = [
                        moment(this.state.orderDetails.notification_start_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Sent to Patient',
                        moment(this.state.orderDetails.notification_checked_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Sent to Physician',
                        moment(this.state.orderDetails.notification_checked_time, 'YYYYMMDDHHmmss').format(
                            'MM/DD/YYYY hh:mm A'
                        ) + ' Delivered to SuperAdmin'
                    ];
                    break;
                default:
                    break;
            }
        }
        if (this.state.orderDetails.noticeHistory && this.state.orderDetails.noticeHistory.length) {
            this.state.orderDetails.noticeHistory.map((item) => {
                let itemTitle = item.title;
                if (itemTitle === 'To Facility For Strep Release') {
                    itemTitle = itemTitle.replace('Strep', this.state.orderDetails.test_id && this.state.orderDetails.test_id.name);
                }
                orderNoticeHistoryStrings.push(
                    moment(item.time, 'YYYYMMDDHHmmss').format('MM/DD/YYYY hh:mm A') +
                        ' - ' +
                        itemTitle +
                        ' - ' +
                        item.destination
                );
                return null;
            });
        }
        if (this.state.orderDetails.sample_tracking && this.state.orderDetails.sample_tracking.length) {
            this.state.orderDetails.sample_tracking.map((item, index) => {
                let plateName = '';
                let wellPosition = '';
                let eventName = item.eventName;
                if (item.plateId) {
                    if (this.props.type === 'universal_search') {
                        (this.props.plateList || []).map((plate) => {
                            if (plate._id === item.plateId) {
                                plateName = plate.name;
                            }
                            return null;
                        });
                    } else {
                        if (
                            this.props.context.componentParent &&
                            this.props.context.componentParent.state &&
                            this.props.context.componentParent.state.plateList
                        ) {
                            this.props.context.componentParent.state.plateList.map((plate) => {
                                if (plate._id === item.plateId) {
                                    plateName = plate.name;
                                }
                                return null;
                            });
                        }
                    }
                    if (item.cellIndex && !plateName.includes('SOL')) {
                        const { row, column } = getRowColumnIndexForQuantPlate(item.cellIndex);
                        wellPosition = quantPlateRows[row] + quantPlateColumns[column];
                    } else if (item.hasOwnProperty('cellIndex')) {
                        wellPosition = Number(item.cellIndex) + 1;
                    }
                }
                if (eventName === 'IMPORT - FOR RETEST') {
                    if (['Positive SARS-CoV-2', 'SARS-CoV-2 POSITIVE'].includes(item.result)) {
                        eventName += ' (Positive)';
                    } else {
                        eventName += ' (Inconclusive / Invalid)';
                    }
                }
                sampleTrackingStrings.push(
                    moment(item.time, 'YYYYMMDDHHmmss').format('MM/DD/YYYY hh:mm A') +
                        ' ' +
                        eventName +
                        (plateName ? ' - ' + plateName + ' ' + (wellPosition ? '(' + wellPosition + ')' : '') : '')
                );
                return null;
            });
        }
        let currentStatus = this.state.orderDetails.status;
        if (this.state.orderDetails.is_cancel) {
            currentStatus = 'Cancelled';
        } else if (
            this.state.orderDetails.passed_24hours &&
            !(
                this.state.orderDetails.sample_tracking &&
                this.state.orderDetails.sample_tracking.filter((item) => item.eventName === 'ACCESSIONED').length
            )
        ) {
            currentStatus = 'Not Received (1 Day)';
        }
        let orderType = 'Test';
        if (
            this.state.orderDetails.results &&
            (this.state.orderDetails.results.value ||
                (this.state.orderDetails.results.values && this.state.orderDetails.results.values.length > 0))
        ) {
            orderType = 'Order';
        }
        if (this.state.orderDetails.order_type === 'root') {
            orderType = 'Order';
        }
        let hl7_status = '';
        if (this.state.orderDetails.is_HL7_requisition) {
            hl7_status += 'Incoming HL7';
        }
        if (this.state.orderDetails.HL7_result_sent_status) {
            hl7_status +=
                (hl7_status ? ' -> ' : '') + 'Outgoing (' + this.state.orderDetails.HL7_result_sent_status + ')';
        }
        const drawerOpen = this.state.showSubOrderDrawer && this.state.subOrderDetails;
        return (
            <>
            {drawerOpen && (
                <style>{`
                    .order-view-modal-shifted .modal-dialog {
                        margin-right: 500px !important;
                        transition: margin-right 0.22s ease;
                        max-width: calc(100vw - 520px) !important;
                    }
                `}</style>
            )}
            <ModalStyled
                size="xl"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                show={this.props.show}
                onHide={this.props.handleClose}
                backdrop="static"
                className={[
                    this.state.orderDetails && this.state.orderDetails.order_type === 'sub_panel' ? 'sub_panel_order_view_popup' : '',
                    drawerOpen ? 'order-view-modal-shifted' : ''
                ].filter(Boolean).join(' ')}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Full Information</Modal.Title>
                    {this.props.onOpenSidePanel && (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            style={{ marginLeft: '1rem' }}
                            onClick={() => {
                                this.props.handleClose();
                                this.props.onOpenSidePanel();
                            }}
                        >
                            <i className="fa fa-columns mr-1" aria-hidden="true"></i> Side Panel
                        </Button>
                    )}
                </Modal.Header>
                <Modal.Body>
                    {this.state.orderDetailsLoading && (
                        <div className="text-center py-5">
                            <span className="spinner-border text-primary" role="status" />
                            <div className="mt-2 text-muted">Loading order details...</div>
                        </div>
                    )}
                    {!this.state.orderDetailsLoading && this.state.orderDetails && this.state.orderDetails.patient_id && (
                        <div>
                            <div className="row">
                                <div className="col-12">
                                    <div>
                                        <b>Status:</b> {orderType} - {currentStatus}{' '}
                                        {this.state.orderDetails.order_type === 'panel'
                                            ? '(Panel)'
                                            : this.state.orderDetails.order_type === 'sub_panel'
                                            ? '(Part of Panel)'
                                            : ''}
                                    </div>
                                </div>
                                <div className="col-md-5">
                                    <div>
                                        <b>Requisition Number:</b>{' '}
                                        <span
                                            style={{ color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline' }}
                                            title="Go to Orders"
                                            onClick={() => { window.location.href = '/clinic/orders?req=' + encodeURIComponent(this.state.requisitionNumber); }}
                                        >
                                            {this.state.requisitionNumber}
                                        </span>
                                    </div>
                                    {isUsingInternalSample() && (
                                        <div>
                                            <b>External Order ID:</b>{' '}
                                            <span
                                                style={{ color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline' }}
                                                title="Click to copy"
                                                onClick={() => { navigator.clipboard && navigator.clipboard.writeText(this.state.orderDetails.external_sample_id); }}
                                            >
                                                {this.state.orderDetails.external_sample_id}
                                            </span>
                                        </div>
                                    )}
                                    <div>
                                        <b>Sample ID:</b>{' '}
                                        {isUsingInternalSample() &&
                                        this.state.orderDetails.external_sample_id ===
                                            this.state.orderDetails.test_info.sample
                                            ? ''
                                            : (
                                                <span
                                                    style={{ color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline' }}
                                                    title="Click to copy"
                                                    onClick={() => { navigator.clipboard && navigator.clipboard.writeText(this.state.orderDetails.test_info.sample); }}
                                                >
                                                    {this.state.orderDetails.test_info.sample}
                                                </span>
                                            )}
                                    </div>
                                    {this.state.orderDetails.is_cancel && this.state.orderDetails.cancel_reason && (
                                        <div>
                                            <b>Cancelled Reason:</b> {this.state.orderDetails.cancel_reason}
                                        </div>
                                    )}
                                    {/* {this.state.orderDetails.results && this.state.orderDetails.results.value && (
                                        <div>
                                            <b>Result:</b>{' '}
                                            <a
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{
                                                    textDecoration: 'underline'
                                                }}
                                                href={
                                                    serviceConstants.API_HOST_NAME +
                                                    this.state.orderDetails.results.pdf_path +
                                                    '?' +
                                                    new Date().getTime() +
                                                    '&token=' +
                                                    getUserAuthToken()
                                                }
                                            >
                                                {this.state.orderDetails.results.value}
                                            </a>
                                        </div>
                                    )}
                                    {((this.state.orderDetails.results && this.state.orderDetails.results.values && this.state.orderDetails.results.values.length > 0) || ['Resulted', 'Pending Review', 'Reviewed', 'Reported'].includes(this.state.orderDetails.status)) && (
                                            <div>
                                                <b>Result:</b>{' '}
                                                <a
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{
                                                        textDecoration: 'underline'
                                                    }}
                                                    href={
                                                        serviceConstants.API_HOST_NAME +
                                                        this.state.orderDetails.results.pdf_path +
                                                        '?' +
                                                        new Date().getTime() +
                                                        '&token=' +
                                                        getUserAuthToken()
                                                    }
                                                >
                                                    Combined Result
                                                </a>
                                            </div>
                                        )} */}
                                    {this.state.orderDetails.results && this.state.orderDetails.results.pdf_path && (
                                        <div>
                                            <b>Result:</b>{' '}
                                            <a
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{
                                                    textDecoration: 'underline'
                                                }}
                                                href={
                                                    serviceConstants.API_HOST_NAME +
                                                    this.state.orderDetails.results.pdf_path +
                                                    '?' +
                                                    new Date().getTime() +
                                                    '&token=' +
                                                    getUserAuthToken()
                                                }
                                            >
                                                Combined Result
                                            </a>
                                        </div>
                                    )}
                                    {this.state.orderDetails.test_info &&
                                        this.state.orderDetails.test_info.received && (
                                            <div>
                                                <b>Received Date:</b>{' '}
                                                {moment(
                                                    this.state.orderDetails.test_info.received,
                                                    'YYYYMMDDHHmmss'
                                                ).format('MM/DD/YYYY hh:mm A')}
                                            </div>
                                        )}
                                    {this.state.orderDetails.results && this.state.orderDetails.results.released && (
                                        <div>
                                            <b>Released Date:</b>{' '}
                                            {moment(this.state.orderDetails.results.released, 'YYYYMMDDHHmmss').format(
                                                'MM/DD/YYYY hh:mm A'
                                            )}
                                        </div>
                                    )}
                                    {this.state.orderDetails.results && this.state.orderDetails.results.released && (
                                        <div>
                                            <b>HL7 Status:</b> {hl7_status}
                                        </div>
                                    )}
                                </div>
                                <div className="col-md-7">
                                    <div>
                                        <b>Patient:</b>{' '}
                                        <span
                                            style={{ color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline' }}
                                            title="View Patient Record"
                                            onClick={() => { this.setState({ showPatientPopup: true }); }}
                                        >
                                            {this.state.orderDetails.patient_id.first_name + ' ' + this.state.orderDetails.patient_id.last_name}
                                        </span>{' '}
                                        (
                                        {moment(this.state.orderDetails.patient_id.date_of_birth, 'YYYY-MM-DD').format(
                                            'MM/DD/YYYY'
                                        )}
                                        )
                                    </div>
                                    <div>
                                        <b>Patient ID:</b>{' '}
                                        <span
                                            style={{ color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline' }}
                                            title="View Patient Medications"
                                            onClick={() => { window.location.href = '/clinic/patientMedications/' + this.state.orderDetails.patient_id._id; }}
                                        >
                                            {this.state.orderDetails.patient_id.mrn}
                                        </span>
                                    </div>
                                    <div>
                                        <b>Phone/Email:</b> {this.state.orderDetails.patient_id.mobile} /{' '}
                                        {this.state.orderDetails.patient_id.email
                                            ? this.state.orderDetails.patient_id.email
                                            : ''}
                                    </div>
                                    <div>
                                        <b>Address:</b> {getFullAddress(this.state.orderDetails.patient_id.address)}
                                    </div>
                                    <div>
                                        <b>Driver License:</b> {this.state.orderDetails.patient_id.driver_license}{' '}
                                        <b>Driver License State:</b>{' '}
                                        {this.state.orderDetails.patient_id.driver_license_state}
                                    </div>
                                </div>
                            </div>
                            <hr />
                            <div className="row">
                                <h4 className="col-12">Order Information</h4>
                                {this.state.orderDetails.orderIds && this.state.orderDetails.orderIds.length > 0 && (
                                    <ThemeContext.Consumer>
                                        {({ themeName }) => (
                                            <div
                                                id="currentListGrid"
                                                style={{
                                                    height: '250px',
                                                    width: '100%',
                                                    marginBottom: '20px'
                                                }}
                                            >
                                                <AgGridReact
                                                    columnDefs={this.state.columnDefs}
                                                    defaultColDef={this.state.defaultColDef}
                                                    masterDetail={true}
                                                    onGridReady={this.onGridReady}
                                                    rowData={this.state.rowData}
                                                    pagination={true}
                                                    paginationPageSize={20}
                                                    paginationNumberFormatter={this.state.paginationNumberFormatter}
                                                    context={this.state.context}
                                                    components={{ subOrderActionRenderer: SubOrderActionRenderer }}
                                                    theme={themeName === 'Light' ? themeLight : themeDark}
                                                    quickFilterText={this.state.quickFilterText}
                                                />
                                            </div>
                                        )}
                                    </ThemeContext.Consumer>
                                )}
                                <div className="col-12 mt-2">
                                    <b>Requisition PDFs:</b>
                                    {this.state.uploadedFiles && this.state.uploadedFiles.length > 0 ? (
                                        <table className="table table-sm table-bordered mt-2" style={{ fontSize: '13px' }}>
                                            <thead className="table-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th>File Name</th>
                                                    <th style={{ width: '80px', textAlign: 'center' }}>Open</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {this.state.uploadedFiles.map((fileName, index) => {
                                                    const fileUrl = serviceConstants.API_HOST_NAME +
                                                        "/files/requisitionPDF/" +
                                                        this.state.requisitionNumber +
                                                        "/" + fileName +
                                                        "?" + new Date().getTime() +
                                                        "&token=" + getUserAuthToken();
                                                    const ext = fileName.split('.').pop().toLowerCase();
                                                    const icon = ext === 'pdf' ? 'fa-file-pdf' : ext === 'png' || ext === 'jpg' || ext === 'jpeg' ? 'fa-file-image' : 'fa-file';
                                                    return (
                                                        <tr key={index}>
                                                            <td>{index + 1}</td>
                                                            <td>
                                                                <i className={`far ${icon} mr-2`} style={{ color: ext === 'pdf' ? '#d9534f' : '#6c757d' }} aria-hidden="true"></i>
                                                                {fileName}
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                <a
                                                                    href={fileUrl}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    title={`Open ${fileName}`}
                                                                    className="btn btn-sm btn-outline-primary"
                                                                >
                                                                    <i className="fas fa-external-link-alt" aria-hidden="true"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <span className="text-muted ml-2">None</span>
                                    )}
                                </div>
                                <div className="col-12">
                                    <b>Test:</b> {this.state.orderDetails.test_id && this.state.orderDetails.test_id.name}
                                </div>
                                <div className="col-12">
                                    <b>Test Type:</b> {this.state.orderDetails.test_info.specimenType}
                                </div>
                                <div className="col-12">
                                    <b>Specimen Collected Date:</b>{' '}
                                    {moment(this.state.orderDetails.test_info.collected, 'YYYYMMDDHHmmss').format(
                                        'MM/DD/YYYY hh:mm A'
                                    )}
                                </div>
                                <div className="col-12 mt-2">
                                    <b>Facility Source:</b> {this.state.orderDetails.facility_source}
                                </div>
                                <div className="col-12 mt-2">
                                    <b>Physician:</b>{' '}
                                    {this.state.orderDetails.provider.first_name +
                                        ' ' +
                                        this.state.orderDetails.provider.last_name}
                                </div>
                                <div className="col-12 mt-2">
                                    <b>Diagnosis Codes:</b> {this.state.orderDetails.diagnosis_code.join(', ')}
                                </div>
                                <div className="col-12 mt-2">
                                    <b>Notification Logs:</b>
                                </div>
                                {notificatinLogStrings.map((string, index) => {
                                    return (
                                        <div className="col-12" key={index}>
                                            {string}
                                        </div>
                                    );
                                })}
                                <div className="col-12 mt-2">
                                    <b>Sample Tracking:</b>
                                </div>
                                {sampleTrackingStrings.map((string, index) => {
                                    return (
                                        <div className="col-12" key={index}>
                                            {string}
                                        </div>
                                    );
                                })}
                                <div className="col-12 mt-2">
                                    <b>Requisition PDF:</b>{' '}
                                    {this.state.orderDetails.requisition_pdf_path ? (
                                        <a
                                            target="_blank"
                                            rel="noreferrer"
                                            title="Open Requisition PDF"
                                            className="btn btn-sm btn-outline-primary ml-2"
                                            href={
                                                serviceConstants.API_HOST_NAME +
                                                '/files/requisitionPDF/' +
                                                this.state.orderDetails.requisition_pdf_path +
                                                '?' +
                                                new Date().getTime() +
                                                '&token=' +
                                                getUserAuthToken()
                                            }
                                        >
                                            <i className="fa fa-file-pdf-o mr-1"></i>
                                            {this.state.orderDetails.requisition_pdf_path}
                                        </a>
                                    ) : (
                                        <span className="text-muted ml-2">None</span>
                                    )}
                                </div>
                                {this.state.orderDetails.symptoms && this.state.orderDetails.symptoms.length > 0 && (
                                    <div className="col-12">
                                        <b>Patient Symptoms:</b> {this.state.orderDetails.symptoms.join(', ')}
                                    </div>
                                )}
                                {this.state.orderDetails.medicationIds &&
                                    this.state.orderDetails.medicationIds.length > 0 && (
                                        <div className="col-12 d-flex align-items-center mt-2">
                                            <b>Medications:</b>
                                        </div>
                                    )}
                                {this.state.orderDetails.medicationIds &&
                                    this.state.orderDetails.medicationIds.length > 0 &&
                                    this.state.orderDetails.medicationIds.map((medicationItem, index) => {
                                        return (
                                            <div className="col-12" key={index}>
                                                <span className="mr-2" style={{ fontWeight: '500' }}>
                                                    {medicationItem.description}
                                                </span>
                                                {medicationItem.diagnosisCodes &&
                                                    medicationItem.diagnosisCodes.length > 0 && (
                                                        <span className="font-italic">
                                                            ({medicationItem.diagnosisCodes.join(', ')})
                                                        </span>
                                                    )}
                                            </div>
                                        );
                                    })}
                                <div className="col-12 mt-2">
                                    <b>Order Notice History:</b>
                                </div>
                                {orderNoticeHistoryStrings.map((string, index) => {
                                    return (
                                        <div className="col-12" key={index}>
                                            {string}
                                        </div>
                                    );
                                })}
                            </div>
                
                            {/* Release & Delivery */}
                            {this.state.orderDetails && (((this.state.orderDetails.release_signatures || []).length) || ((this.state.orderDetails.delivery_log || []).length)) ? (
                                <div className="col-12 mt-3 mb-2">
                                    <div style={{ border: '1px solid #dee2e6', borderRadius: 8, padding: 16 }}>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h4 className="mb-0">Release &amp; Delivery</h4>
                                            <button className="btn btn-sm btn-outline-success" onClick={() => {
                                                resendDelivery(this.state.orderDetails._id).then((res) => {
                                                    if (res && res.success) {
                                                        toastr.success(res.message || 'Resent');
                                                        this.setState({ orderDetails: { ...this.state.orderDetails, delivery_log: res.data.delivery_log } });
                                                    } else toastr.error((res && res.message) || 'Resend failed');
                                                }).catch(() => toastr.error('Resend failed'));
                                            }}><i className="fas fa-paper-plane mr-1"></i>Resend</button>
                                        </div>
                                        {(this.state.orderDetails.release_signatures || []).map((s, i) => (
                                            <div key={i} className="mb-2" style={{ fontSize: 13 }}>
                                                <i className="fas fa-check-circle mr-1" style={{ color: '#198754' }}></i>
                                                Electronically signed by <b>{s.name}</b> &mdash; {s.meaning} &mdash; {s.signed_at ? new Date(s.signed_at).toLocaleString() : ''} <span className="text-muted">({s.method})</span>
                                            </div>
                                        ))}
                                        <table className="table table-sm table-bordered mb-0" style={{ fontSize: 13 }}>
                                            <thead className="table-light"><tr><th>Channel</th><th>Status</th><th>Detail</th><th>When</th></tr></thead>
                                            <tbody>
                                                {(this.state.orderDetails.delivery_log || []).slice().reverse().map((d, i) => (
                                                    <tr key={i}>
                                                        <td>{d.channel}</td>
                                                        <td><span style={{ background: { sent: '#198754', available: '#198754', queued: '#0d6efd', failed: '#dc3545' }[d.status] || '#6c757d', color: '#fff', borderRadius: 4, padding: '1px 8px', fontSize: 12, fontWeight: 600 }}>{d.status}</span></td>
                                                        <td>{d.detail || ''}</td>
                                                        <td>{d.at ? new Date(d.at).toLocaleString() : ''}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : null}
                            {/* AI Clinical Narrative */}
                            {((this.state.orderDetails.results && this.state.orderDetails.results.values && this.state.orderDetails.results.values.length > 0) || ['Resulted', 'Pending Review', 'Reviewed', 'Reported'].includes(this.state.orderDetails.status)) && (
                                <>
                                    <hr />
                                    <div className="row">
                                        <div className="col-12 d-flex align-items-center mb-2">
                                            <h4 className="mb-0 mr-3" style={{ display: 'inline' }}>AI Clinical Narrative</h4>
                                            <button
                                                className="btn btn-sm btn-outline-primary ml-2"
                                                onClick={this.handleGenerateNarrative}
                                                disabled={this.state.aiNarrativeLoading}
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                {this.state.aiNarrativeLoading ? (
                                                    <><span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>Generating…</>
                                                ) : this.state.aiNarrative ? '↻ Regenerate' : '✦ Generate Narrative'}
                                            </button>
                                        </div>
                                        {this.state.aiNarrativeError && (
                                            <div className="col-12">
                                                <div className="alert alert-danger py-2">{this.state.aiNarrativeError}</div>
                                            </div>
                                        )}
                                        {this.state.aiNarrative && (
                                            <div className="col-12">
                                                <div style={{ background: 'linear-gradient(135deg,#f0f7ff 0%,#e8f4fd 100%)', border: '1px solid #b8d9f5', borderLeft: '4px solid #2196F3', borderRadius: '6px', padding: '14px 16px', lineHeight: '1.65', fontSize: '0.95rem', color: '#1a2a3a', whiteSpace: 'pre-wrap' }}>
                                                    {this.state.aiNarrative}
                                                </div>
                                                {this.state.aiNarrativeGeneratedAt && (
                                                    <div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}>
                                                        Generated {new Date(this.state.aiNarrativeGeneratedAt).toLocaleString()}{this.state.aiNarrativeModel ? ' · ' + this.state.aiNarrativeModel : ''} · For clinical correlation only.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {!this.state.aiNarrative && !this.state.aiNarrativeLoading && !this.state.aiNarrativeError && (
                                            <div className="col-12 text-muted" style={{ fontSize: '0.88rem' }}>
                                                Click <em>Generate Narrative</em> to produce an AI-assisted clinical summary.
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            <hr />
                            <div className="row">
                                <h4 className="col-12">Payment Information</h4>
                                <div className="col-12">
                                    <b>Insurance Provider:</b>{' '}
                                    {this.state.orderDetails.patient_insurance_id &&
                                    this.state.orderDetails.patient_insurance_id.insurance_provider
                                        ? this.state.orderDetails.patient_insurance_id.insurance_provider
                                        : ''}
                                </div>
                                <div className="col-12 mt-2">
                                    <b>Insurance Member ID:</b>{' '}
                                    {this.state.orderDetails.patient_insurance_id &&
                                    this.state.orderDetails.patient_insurance_id.insured_member_id
                                        ? this.state.orderDetails.patient_insurance_id.insured_member_id
                                        : ''}
                                </div>
                                <div className="col-12">
                                    <b>Insurance Group:</b>{' '}
                                    {this.state.orderDetails.patient_insurance_id &&
                                    this.state.orderDetails.patient_insurance_id.insured_group_number
                                        ? this.state.orderDetails.patient_insurance_id.insured_group_number
                                        : ''}
                                </div>
                                <div className="col-12">
                                    <b>Relation to Insurance</b>{' '}
                                    {this.state.orderDetails.patient_insurance_id &&
                                    this.state.orderDetails.patient_insurance_id.relation_to_insured
                                        ? this.state.orderDetails.patient_insurance_id.relation_to_insured
                                        : ''}
                                </div>
                                <div className="col-12">
                                    <b>Insurance Holder:</b>{' '}
                                    {this.state.orderDetails.patient_insurance_id &&
                                    this.state.orderDetails.patient_insurance_id.insured_first_name
                                        ? this.state.orderDetails.patient_insurance_id.insured_first_name
                                        : ''}{' '}
                                    {this.state.orderDetails.patient_insurance_id &&
                                    this.state.orderDetails.patient_insurance_id.insured_last_name
                                        ? this.state.orderDetails.patient_insurance_id.insured_last_name
                                        : ''}
                                </div>
                                <div className="col-12">
                                    <b>Social Security:</b>{' '}
                                    {this.state.orderDetails.patient_insurance_id &&
                                    this.state.orderDetails.patient_insurance_id.social_security_number
                                        ? this.state.orderDetails.patient_insurance_id.social_security_number
                                        : ''}
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.props.handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </ModalStyled>
            {/* Side-panel drawer — slides in from the right alongside the modal */}
            {this.state.showSubOrderDrawer && this.state.subOrderDetails && (
                <SubOrderDrawer
                    orderDetails={this.state.subOrderDetails}
                    onClose={() => this.setState({ showSubOrderDrawer: false, subOrderDetails: null })}
                />
            )}
            {this.state.orderDetails && this.state.orderDetails.patient_id && (
                <PatientViewPopup
                    show={this.state.showPatientPopup}
                    data={this.state.orderDetails.patient_id}
                    handleClose={() => this.setState({ showPatientPopup: false })}
                />
            )}
            </>
        );
    }
}
